import { Logger } from "../utils/Logger";
import { StreamMediaType } from "./StreamMediaType";
import { validateWritingCookie } from "../utils/Cookies";
import { validateWritingLocalStorage } from "../utils/LocalStorage";

/**
 * Interface used to specify the contract that each implementation of the library must fulfill.
 */
export abstract class GtmDataSyncLib {
  /**
   * Set up / augment the user in the GTM DataLayer based on:
   * - Data from the UIE system
   * - Data from Lotame
   * - Data from Adobe's ID Service API
   * - Data from the CDP (BlueConic)
   * The data sources depends on the site.
   */
  abstract setupCurrentUser(): Promise<void>;

  // noinspection JSUnusedGlobalSymbols
  /**
   * Set the lotame-specific identifier of the current user in the UIE system.
   * @remarks
   * This method is currently called by the lotame_logic_audience GTM tag.
   * Lotame's tags may load before this library load. Therefore, the call to UIE may be delayed until
   * the library is loaded in the identity of the current user is loaded from the UIE.
   * @param lotameId
   * The identifier of the current user in Lotame.
   */
  abstract setLotameIdInUie(lotameId: string): Promise<void>;

  /**
   * In Single Page Applications (like ici.radio-canada.ca), call this method to run whatever code should be run
   * when a page view is triggered (but this library is already loaded!)
   */
  abstract handlePageView(): Promise<void>;

  // noinspection JSUnusedGlobalSymbols
  /**
   * Invoke this method from GTM when a "read" event is triggered.
   */
  abstract handleReadEvent(): Promise<void>;

  // noinspection JSUnusedGlobalSymbols
  /**
   * Invoke this method from GTM when a "trackContentStart" event is triggered.
   * @param mediaType The type of media, either audio or video.
   */
  abstract handleContentStart(mediaType: StreamMediaType): Promise<void>;

  // noinspection JSUnusedGlobalSymbols
  /**
   * Invoke this method from GTM when a "consentChanged" event is triggered.
   */
  abstract handleConsentChanged(): Promise<void>;

  // noinspection JSUnusedGlobalSymbols
  /**
   * Send an error message. Called from a GTM tag
   * Will send the logs to kibana
   */
  public sendErrorLog(message: string): void {
    Logger.error(message);
    return;
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * Send a warning message. Called from a GTM tag
   * Will send the logs to kibana
   */
  public sendWarningLog(message: string): void {
    Logger.warn(message);
    return;
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * Send a verbose message. Called from a GTM tag
   * Will not send the logs to kibana
   */
  public verboseLog(message: string): void {
    Logger.verbose(message);
    return;
  }

  // noinspection JSUnusedGlobalSymbols
  /**
   * Return the build number for this library.
   */
  public buildNumber(): string {
    return BuildNumber;
  }

  /**
   * Validate that the browser can write and read from the browser's storage (cookies & localStorage)
   * For logging purpose, but with the goal of adding a kill-switch.
   */
  public validateStorageAccess(): boolean {
    let isValid = true;
    if (!validateWritingCookie() || !validateWritingLocalStorage()) {
      isValid = false;
    }

    return isValid;
  }
}
